import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { DashboardMetrics, WorkflowState, CompletionStatus } from '../types';
import { adminApiService } from '../services/api';
import { Layout } from '../components/Layout';
import { useAuth } from '../auth/AuthProvider';

const workflowStateColors: Record<WorkflowState, string> = {
  started: '#2196f3',
  submitted: '#4caf50',
  processed: '#ff9800',
  completed: '#9c27b0',
};

const completionStatusColors: Record<CompletionStatus, string> = {
  uploaded: '#9c27b0',
  upload_failed: '#f44336',
  canceled: '#757575',
};


export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only load metrics if authenticated and auth is not loading
    if (isAuthenticated && !authLoading) {
      loadDashboardMetrics();
    }
  }, [isAuthenticated, authLoading]);

  // Poll for updated metrics every 60 seconds
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;

    const interval = setInterval(() => {
      loadDashboardMetrics(true);
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, authLoading]);

  const loadDashboardMetrics = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      const response = await adminApiService.getDashboardMetrics();
      if (response.success && response.data) {
        setMetrics(response.data);
      } else {
        setError(response.error || 'Failed to load dashboard metrics');
      }
    } catch (err) {
      if (!silent) {
        setError('An error occurred while loading dashboard metrics');
      }
      console.error('Dashboard metrics error:', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Navigation functions
  const navigateToJobsWithFilter = (workflowState?: WorkflowState, completionStatus?: CompletionStatus) => {
    const params = new URLSearchParams();
    
    if (workflowState) {
      params.set('workflowState', workflowState);
    }
    
    if (completionStatus) {
      params.set('completionStatus', completionStatus);
      // If filtering by completion status, also set workflow state to completed
      params.set('workflowState', 'completed');
    }
    
    navigate(`/jobs?${params.toString()}`);
  };




  // Calculate the time period being displayed (20 business days)
  const getDataPeriodDescription = () => {
    const today = new Date();
    const lookbackDate = new Date(today);
    let businessDaysBack = 0;
    
    while (businessDaysBack < 20) {
      lookbackDate.setDate(lookbackDate.getDate() - 1);
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (lookbackDate.getDay() !== 0 && lookbackDate.getDay() !== 6) {
        businessDaysBack++;
      }
    }
    
    return {
      startDate: lookbackDate.toLocaleDateString(),
      endDate: today.toLocaleDateString(),
      businessDays: 20
    };
  };

  const dataPeriod = getDataPeriodDescription();

  if (loading) {
    return (
      <Layout pageTitle="Dashboard">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout pageTitle="Dashboard">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Layout>
    );
  }

  if (!metrics) {
    return (
      <Layout pageTitle="Dashboard">
        <Alert severity="info" sx={{ mb: 2 }}>
          No metrics data available
        </Alert>
      </Layout>
    );
  }

  // Only render dashboard content if metrics are available
  const workflowSteps = metrics ? [
    {
      state: 'started' as WorkflowState,
      label: 'Started',
      description: 'Reporter accessed worksheet',
      count: metrics.jobsByState.started || 0,
      color: workflowStateColors.started
    },
    {
      state: 'submitted' as WorkflowState,
      label: 'Submitted',
      description: 'Worksheet submitted',
      count: metrics.jobsByState.submitted || 0,
      color: workflowStateColors.submitted
    },
    {
      state: 'processed' as WorkflowState,
      label: 'Processed',
      description: 'AI processing complete',
      count: metrics.jobsByState.processed || 0,
      color: workflowStateColors.processed
    }
  ] : [];

  const completionCounts = metrics?.completionStatusCounts || {
    uploaded: 0,
    upload_failed: 0,
    canceled: 0
  };
  
  const totalCompleted = metrics?.jobsByState.completed || 0;

  return (
    <Layout 
      pageTitle="Dashboard"
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Showing jobs with activity in the last {dataPeriod.businessDays} business days
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Data period: {dataPeriod.startDate} - {dataPeriod.endDate}
        </Typography>
      </Box>

      {/* Workflow Visualization */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }}>
            Job Workflow Status
          </Typography>
          
          {/* Main workflow circles - all aligned */}
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            flexWrap="wrap"
            sx={{ gap: 4, mb: 4 }}
          >
            {/* Regular workflow steps */}
            {workflowSteps.map((step, index) => (
              <React.Fragment key={step.state}>
                <Box 
                  display="flex" 
                  flexDirection="column" 
                  alignItems="center"
                  sx={{ minWidth: 140 }}
                >
                  <Box
                    onClick={() => navigateToJobsWithFilter(step.state)}
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: step.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      boxShadow: 2,
                      cursor: 'pointer',
                      transition: 'transform 0.2s, boxShadow 0.2s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <Typography variant="h4" color="white" fontWeight="bold">
                      {step.count}
                    </Typography>
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {step.label}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    textAlign="center"
                    sx={{ maxWidth: 120 }}
                  >
                    {step.description}
                  </Typography>
                </Box>
                
                {index < workflowSteps.length - 1 && (
                  <ArrowForwardIcon 
                    sx={{ 
                      fontSize: 32, 
                      color: 'action.active',
                      display: { xs: 'none', md: 'block' }
                    }} 
                  />
                )}
              </React.Fragment>
            ))}
            
            {/* Arrow to Completed */}
            <ArrowForwardIcon 
              sx={{ 
                fontSize: 32, 
                color: 'action.active',
                display: { xs: 'none', md: 'block' }
              }} 
            />
            
            {/* Completed section - aligned with others */}
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center"
              sx={{ minWidth: 140 }}
            >
              <Box
                onClick={() => navigateToJobsWithFilter('completed')}
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: workflowStateColors.completed,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  boxShadow: 2,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, boxShadow 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: 4
                  }
                }}
              >
                <Typography variant="h4" color="white" fontWeight="bold">
                  {totalCompleted}
                </Typography>
              </Box>
              <Typography variant="h6" gutterBottom>
                Completed
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                textAlign="center"
                sx={{ maxWidth: 120 }}
              >
                Processing finished
              </Typography>
            </Box>
          </Box>

          {/* Completion sub-statuses breakdown - below main flow */}
          <Box 
            display="flex" 
            justifyContent="center"
            sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
          >
            <Box>
              <Typography variant="h6" gutterBottom textAlign="center">
                Completion Details
              </Typography>
              <Box 
                display="flex" 
                justifyContent="center"
                gap={4}
                flexWrap="wrap"
              >
                <Box 
                  display="flex" 
                  alignItems="center" 
                  gap={1}
                  onClick={() => navigateToJobsWithFilter(undefined, 'uploaded')}
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: 1,
                    transition: 'backgroundColor 0.2s',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: completionStatusColors.uploaded
                    }}
                  />
                  <Typography variant="body1">
                    Uploaded: {completionCounts.uploaded}
                  </Typography>
                </Box>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  gap={1}
                  onClick={() => navigateToJobsWithFilter(undefined, 'upload_failed')}
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: 1,
                    transition: 'backgroundColor 0.2s',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: completionStatusColors.upload_failed
                    }}
                  />
                  <Typography variant="body1">
                    Failed: {completionCounts.upload_failed}
                  </Typography>
                </Box>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  gap={1}
                  onClick={() => navigateToJobsWithFilter(undefined, 'canceled')}
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: 1,
                    transition: 'backgroundColor 0.2s',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: completionStatusColors.canceled
                    }}
                  />
                  <Typography variant="body1">
                    Canceled: {completionCounts.canceled}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            textAlign="center"
            sx={{ mt: 4, fontStyle: 'italic' }}
          >
            Jobs progress through these stages in the reporter portal workflow
          </Typography>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card 
            onClick={() => navigate('/jobs')}
            sx={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s, boxShadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4
              }
            }}
          >
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Jobs
              </Typography>
              <Typography variant="h4" component="div">
                {metrics?.totalJobs || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card 
            onClick={() => {
              const params = new URLSearchParams();
              params.set('workflowState', 'started,submitted,processed');
              navigate(`/jobs?${params.toString()}`);
            }}
            sx={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s, boxShadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4
              }
            }}
          >
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                In Progress
              </Typography>
              <Typography variant="h4" component="div">
                {(metrics?.jobsByState.started || 0) + (metrics?.jobsByState.submitted || 0) + (metrics?.jobsByState.processed || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card 
            onClick={() => navigateToJobsWithFilter('completed')}
            sx={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s, boxShadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4
              }
            }}
          >
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completion Rate
              </Typography>
              <Typography variant="h4" component="div">
                {metrics?.totalJobs && metrics.totalJobs > 0 ? Math.round(((metrics.jobsByState.completed || 0) / metrics.totalJobs) * 100) : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Layout>
  );
};